<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";

import ABTestVariantDTO = App.Data.ABTest.ABTestVariantDTO;
import { required } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import Switch from "@/ui/components/switch/switch.vue";

const { t } = useI18n();
const test = defineModel<App.Data.ABTest.ABTestDTO>();
const isValid = defineModel<boolean>("valid");
const activeVariantUuid = ref<string | null>(null);
const tempPercentage = ref<Record<string, string>>({});

const autoBalanceEnabled = ref(true);
const formValid = ref(true);

// Calculate total percentage for validation
const totalPercentage = computed(() => {
	if (!test.value || !test.value.variants || test.value.variants.length === 0)
		return 0;
	return test.value.variants.reduce((sum, v) => sum + v.percentage, 0);
});

const percentageError = computed(() => {
	if (autoBalanceEnabled.value) return null;

	const total = totalPercentage.value;
	if (Math.abs(total - 100) < 0.1) return null;

	return total > 100
		? `Total is ${total.toFixed(1)}% (${(total - 100).toFixed(1)}% over 100%)`
		: `Total is ${total.toFixed(1)}% (${(100 - total).toFixed(1)}% under 100%)`;
});

watch([percentageError, formValid], ([e, f]) => {
	isValid.value = e === null && f;
});

const variantColors = [
	"#1976D2",
	"#2196F3",
	"#64B5F6",
	"#90CAF9",
	"#42A5F5",
	"#2979FF",
	"#448AFF",
	"#82B1FF",
];

/** Toggle the lock status of a variant */
function toggleLock(variant: ABTestVariantDTO): void {
	variant.is_locked = !variant.is_locked;
	if (autoBalanceEnabled.value) {
		balancePercentages();
	}
}

/** Set a variant as the control variant */
function setAsControl(variant: ABTestVariantDTO): void {
	if (!test.value) return;

	test.value.variants.forEach((v) => (v.is_control = false));
	variant.is_control = true;
}

/** Initialize the temporary percentage value for editing */
function openPercentageMenu(variant: ABTestVariantDTO): void {
	tempPercentage.value[variant.uuid] = variant.percentage.toFixed(1);
}

/** Apply the edited percentage value to a variant */
function applyPercentage(variant: ABTestVariantDTO): void {
	if (!test.value || !tempPercentage.value[variant.uuid]) return;

	const value = parseFloat(tempPercentage.value[variant.uuid]) || 0;

	if (autoBalanceEnabled.value) {
		// With balancer: Calculate available space based on locked variants
		const lockedTotal = test.value.variants
			.filter((v) => v.is_locked && v.uuid !== variant.uuid)
			.reduce((sum, v) => sum + v.percentage, 0);

		const maxAllowed = 100 - lockedTotal;
		const clampedValue = Math.max(0, Math.min(maxAllowed, value));

		updateVariantPercentage(variant, parseFloat(clampedValue.toFixed(1)));
	} else {
		// Without balancer: Just set the value directly (clamped between 0-100)
		variant.percentage = Number(Math.max(0, Math.min(100, value)).toFixed(1));
	}
}

/** Add a new variant to the test */
function addVariant(): void {
	if (!test.value) return;

	const variants = test.value.variants;
	const newUuid = `n/a-${Date.now()}`; // Gets defined by the backend later

	if (variants.length === 0) {
		variants.push({
			uuid: newUuid,
			name: "Control",
			percentage: 100,
			is_control: true,
			is_locked: false,
		});
		return;
	}

	if (autoBalanceEnabled.value) {
		variants.push({
			uuid: newUuid,
			name: `Variant ${variants.length + 1}`,
			percentage: 0,
			is_control: false,
			is_locked: false,
		});
		balancePercentages();
		return;
	}

	const lockedTotal = variants.reduce(
		(sum, v) => sum + (v.is_locked ? v.percentage : 0),
		0
	);

	const availableSpace = 100 - lockedTotal;
	if (availableSpace <= 0) return;

	const newShare = Math.min(10, availableSpace / 10);
	redistributeFromUnlocked(newShare);
	variants.push({
		uuid: newUuid,
		name: `Variant ${variants.length + 1}`,
		percentage: newShare,
		is_control: false,
		is_locked: false,
	});

	balancePercentages();
}

/** Remove a variant from the test */
function removeVariant(uuid: string): void {
	if (!test.value) return;

	const variants = test.value.variants;
	const index = variants.findIndex((v) => v.uuid === uuid);
	if (index === -1) return;

	const removedVariant = variants[index];
	const isRemovingControl = removedVariant.is_control;
	const shareToRedistribute = removedVariant.percentage;
	const isRemovingLocked = removedVariant.is_locked;

	// Remove the variant
	variants.splice(index, 1);

	if (variants.length > 0) {
		if (autoBalanceEnabled.value) {
			// With balancer: Redistribute the removed variant's percentage
			if (!isRemovingLocked) {
				redistributeToUnlocked(shareToRedistribute);
			} else if (variants.every((v) => v.is_locked)) {
				// If all remaining variants are locked, unlock the first one
				variants[0].is_locked = false;
			}

			balancePercentages();
		}

		// If we removed the control variant, make another one the control
		if (isRemovingControl && variants.length > 0) {
			variants[0].is_control = true;
		}
	}

	delete tempPercentage.value[uuid];
}

/** Update a variant's percentage and rebalance other */
function updateVariantPercentage(
	variant: ABTestVariantDTO,
	percentage: number
): void {
	if (!test.value || variant.is_locked) return;

	if (autoBalanceEnabled.value) {
		// With balancer: Calculate and distribute
		const lockedTotal = test.value.variants
			.filter((v) => v.is_locked && v.uuid !== variant.uuid)
			.reduce((sum, v) => sum + v.percentage, 0);

		// Calculate maximum available percentage
		const maxAvailable = 100 - lockedTotal;
		const clampedPercentage = Math.min(percentage, maxAvailable);

		activeVariantUuid.value = variant.uuid;
		variant.percentage = clampedPercentage;
		balancePercentages();
		activeVariantUuid.value = null;
	} else {
		// Without balancer: Just set the value directly
		variant.percentage = Number(
			Math.max(0, Math.min(100, percentage)).toFixed(1)
		);
	}
}

/** Take percentage from unlocked variants proportionally */
function redistributeFromUnlocked(percentageNeeded: number): void {
	if (!test.value || !autoBalanceEnabled.value) return;

	const unlockedVariants = test.value.variants.filter((v) => !v.is_locked);
	const unlockedTotal = unlockedVariants.reduce(
		(sum, v) => sum + v.percentage,
		0
	);

	if (unlockedTotal <= 0) return;

	unlockedVariants.forEach((variant) => {
		const reductionRatio =
			(variant.percentage / unlockedTotal) * percentageNeeded;
		variant.percentage = Math.max(
			0,
			Number((variant.percentage - reductionRatio).toFixed(1))
		);
	});
}

function redistributeToUnlocked(percentage: number): void {
	if (!test.value || !autoBalanceEnabled.value) return;

	const unlockedVariants = test.value.variants.filter((v) => !v.is_locked);
	if (unlockedVariants.length === 0) return;

	const unlockedSum = unlockedVariants.reduce(
		(sum, v) => sum + v.percentage,
		0
	);

	if (unlockedSum > 0) {
		// Distribute proportionally to current percentages
		unlockedVariants.forEach((v) => {
			const ratio = v.percentage / unlockedSum;
			v.percentage += percentage * ratio;
		});
	} else {
		// If all unlocked variants are at 0%, distribute equally
		const equalShare = percentage / unlockedVariants.length;
		unlockedVariants.forEach((v) => {
			v.percentage = equalShare;
		});
	}
}

function balancePercentages(): void {
	if (
		!test.value ||
		test.value.variants.length === 0 ||
		!autoBalanceEnabled.value
	)
		return;

	const variants = test.value.variants;

	// Special case: only one variant gets 100%
	if (variants.length === 1) {
		variants[0].percentage = 100;
		return;
	}

	const lockedVariants = variants.filter((v) => v.is_locked);
	const unlockedVariants = variants.filter((v) => !v.is_locked);
	const lockedTotal = lockedVariants.reduce((sum, v) => sum + v.percentage, 0);
	const availableSpace = 100 - lockedTotal;
	if (unlockedVariants.length === 0) return;

	if (unlockedVariants.length === 1) {
		unlockedVariants[0].percentage = Math.max(
			0,
			Number(availableSpace.toFixed(1))
		);
		return;
	}

	if (availableSpace <= 0) {
		unlockedVariants.forEach((v) => (v.percentage = 0));
		return;
	}

	// If we're adjusting a specific variant
	if (activeVariantUuid.value) {
		const activeVariant = variants.find(
			(v) => v.uuid === activeVariantUuid.value
		);
		if (!activeVariant || activeVariant.is_locked) return;

		activeVariant.percentage = Math.min(
			activeVariant.percentage,
			availableSpace
		);

		const otherUnlocked = unlockedVariants.filter(
			(v) => v.uuid !== activeVariantUuid.value
		);
		const remainingSpace = availableSpace - activeVariant.percentage;

		if (remainingSpace <= 0) {
			otherUnlocked.forEach((v) => (v.percentage = 0));
			return;
		}

		distributeRemainingSpace(otherUnlocked, remainingSpace);
	} else {
		// No active variant - scale all unlocked variants proportionally
		distributeRemainingSpace(unlockedVariants, availableSpace);
	}

	// Final adjustment to ensure exactly 100%
	const finalTotal = variants.reduce((sum, v) => sum + v.percentage, 0);
	if (Math.abs(finalTotal - 100) > 0.1 && unlockedVariants.length > 0) {
		const diff = 100 - finalTotal;
		for (const v of unlockedVariants) {
			if (v.percentage + diff >= 0) {
				v.percentage = Number((v.percentage + diff).toFixed(1));
				break;
			}
		}
	}
}

function distributeRemainingSpace(
	variants: ABTestVariantDTO[],
	availableSpace: number
): void {
	const totalPercentage = variants.reduce((sum, v) => sum + v.percentage, 0);

	if (totalPercentage <= 0) {
		// If all variants are at 0%, distribute equally
		const equalShare = availableSpace / variants.length;
		variants.forEach((v) => (v.percentage = Number(equalShare.toFixed(1))));
	} else {
		// Scale proportionally
		const scaleFactor = availableSpace / totalPercentage;
		variants.forEach(
			(v) => (v.percentage = Number((v.percentage * scaleFactor).toFixed(1)))
		);
	}
}

function getVariantColor(index: number): string {
	return variantColors[index % variantColors.length];
}

onMounted(() => {
	if (!test.value) return;

	const variants = test.value.variants;

	// Set a default control variant if none exists
	if (variants.length > 0 && !variants.some((v) => v.is_control)) {
		const control =
			variants.find((v) => v.name.toLowerCase().includes("control")) ||
			variants[0];
		control.is_control = true;
	}

	variants.forEach((v) => {
		if (v.is_locked === undefined) {
			v.is_locked = false;
		}
	});
});
</script>

<template>
	<v-card rounded class="ab-test-card" elevation="0">
		<!-- Card Header -->
		<v-card-item class="pa-4">
			<div class="panel-header-content">
				<div class="test-info">
					<div class="test-title">
						<v-icon icon="fa-solid fa-flask" class="test-icon" size="small" />
						<span class="test-name">{{ test.name }}</span>
					</div>
					<span v-if="test.description" class="test-description">
						{{ test.description }}
					</span>
				</div>
				<div class="d-flex align-center ga-2">
					<Switch
						v-model="autoBalanceEnabled"
						@update:modelValue="(value) => value && balancePercentages()"
						:label="autoBalanceEnabled ? 'Auto-Balance On' : 'Auto-Balance Off'"
					/>
					<v-divider vertical />
					<v-btn
						v-tooltip="'Add Variant'"
						variant="text"
						density="comfortable"
						size="32"
						@click.stop="addVariant"
						color="primary"
						class="border"
					>
						<v-icon icon="fa-solid fa-plus" size="small" />
					</v-btn>
				</div>
			</div>
		</v-card-item>

		<v-divider></v-divider>

		<!-- Card Content -->
		<v-card-text class="panel-content">
			<div v-if="!test?.variants?.length" class="no-variants">
				<v-icon icon="fa-solid fa-info-circle" color="info" class="mr-2" />
				No variants defined yet
			</div>

			<template v-else>
				<v-form v-model="formValid">
					<div class="variants-list">
						<!-- Variant Items -->
						<div
							v-for="(variant, index) in test.variants"
							:key="index"
							class="variant-item"
							:class="{
								'control-variant': variant.is_control,
								'locked-variant': variant.is_locked,
							}"
						>
							<!-- Variant Info Section -->
							<div class="variant-info">
								<v-checkbox
									v-model="variant.is_control"
									density="compact"
									color="success"
									hide-details
									class="control-checkbox"
									v-tooltip="`Set as Control variant`"
									@update:model-value="setAsControl(variant)"
									:disabled="variant.is_control"
								/>

								<v-text-field
									v-model.trim="variant.name"
									density="compact"
									label="Variant name"
									variant="outlined"
									hide-details="auto"
									bg-color="surface"
									:rules="[required(t)]"
									class="variant-name-field"
								/>

								<!-- Percentage Controls -->
								<div class="percentage-actions">
									<!-- Lock Button -->
									<v-btn
										v-tooltip="
											variant.is_locked
												? 'Unlock percentage'
												: 'Lock percentage'
										"
										variant="text"
										density="comfortable"
										size="32"
										@click.stop="toggleLock(variant)"
										:color="variant.is_locked ? 'warning' : 'grey'"
										class="lock-btn"
										:disabled="!autoBalanceEnabled"
									>
										<v-icon
											:icon="variant.is_locked ? 'mdi-lock' : 'mdi-lock-open'"
											size="large"
										/>
									</v-btn>

									<!-- Percentage Chip with Menu -->
									<div class="percentage-control">
										<v-menu
											:close-on-content-click="false"
											transition="scale-transition"
											min-width="200"
											@click:outside="applyPercentage(variant)"
											:disabled="variant.is_locked && autoBalanceEnabled"
										>
											<!-- Chip with Percentage Display -->
											<template v-slot:activator="{ props }">
												<v-chip
													v-bind="props"
													:color="
														variant.is_control
															? 'success'
															: variant.is_locked
															? 'warning'
															: 'primary'
													"
													text-color="white"
													size="small"
													class="variant-chip"
													variant="elevated"
													@click="openPercentageMenu(variant)"
													v-tooltip="
														variant.is_locked && autoBalanceEnabled
															? 'Percentage is locked'
															: 'Click to edit percentage'
													"
													:disabled="variant.is_locked && autoBalanceEnabled"
												>
													{{ variant.percentage.toFixed(1) }}%

													<v-icon size="large" class="ml-1" icon="mdi-pen" />
												</v-chip>
											</template>

											<!-- Percentage Edit Form -->
											<v-text-field
												:id="`percentage-input-${variant.uuid}`"
												v-model="tempPercentage[variant.uuid]"
												density="compact"
												hide-details
												type="number"
												step="0.1"
												min="0"
												max="100"
												suffix="%"
												variant="solo"
												hide-spin-buttons
												label="Percentage"
												class="percentage-input"
												bg-color="surface"
												autofocus
												@keydown.enter="applyPercentage(variant)"
												@keydown.esc="$event.target.blur()"
											/>
										</v-menu>
									</div>
								</div>
							</div>

							<!-- Percentage Slider -->
							<div class="slider-container">
								<v-slider
									v-model="variant.percentage"
									min="0"
									max="100"
									step="0.5"
									tick-size="4"
									density="compact"
									:color="
										variant.is_control
											? 'success'
											: variant.is_locked
											? 'warning'
											: 'primary'
									"
									track-color="grey-lighten-3"
									hide-details
									class="variant-slider"
									@update:model-value="
										(m) => updateVariantPercentage(variant, m)
									"
									:disabled="variant.is_locked && autoBalanceEnabled"
									:readonly="variant.is_locked && autoBalanceEnabled"
								/>
							</div>

							<!-- Remove Button -->
							<div class="variant-actions">
								<v-btn
									v-tooltip="'Remove Variant'"
									variant="tonal"
									size="32"
									density="comfortable"
									color="error"
									class="action-btn-small"
									@click.stop="removeVariant(variant.uuid)"
									:disabled="test.variants.length <= 1"
								>
									<v-icon icon="fa-solid fa-xmark" size="x-small" />
								</v-btn>
							</div>
						</div>
					</div>
				</v-form>

				<!-- Percentage Summary Bar -->
				<div class="variants-summary mt-4">
					<div class="percentage-bar">
						<div
							v-for="(variant, index) in test.variants"
							:key="`bar-${variant.uuid}`"
							class="percentage-segment"
							:class="{
								'control-segment': variant.is_control,
								'locked-segment': variant.is_locked && autoBalanceEnabled,
								'error-bar':
									!autoBalanceEnabled && Math.abs(totalPercentage - 100) > 0.1,
							}"
							:style="{
								width: `${variant.percentage}%`,
								backgroundColor:
									variant.is_locked && autoBalanceEnabled
										? '#FB8C00'
										: variant.is_control
										? '#4CAF50'
										: getVariantColor(index),
							}"
						/>
					</div>

					<!-- Variant Labels -->
					<div class="variant-labels mt-1">
						<div
							v-for="(variant, index) in test.variants"
							:key="`label-${variant.uuid}`"
							class="variant-label"
							:class="{
								'control-label': variant.is_control,
								'locked-label': variant.is_locked && autoBalanceEnabled,
							}"
							:style="{
								color:
									variant.is_locked && autoBalanceEnabled
										? '#FB8C00'
										: variant.is_control
										? '#4CAF50'
										: getVariantColor(index),
							}"
						>
							<span
								class="color-dot"
								:style="{
									backgroundColor:
										variant.is_locked && autoBalanceEnabled
											? '#FB8C00'
											: variant.is_control
											? '#4CAF50'
											: getVariantColor(index),
								}"
							/>
							{{ variant.name }} ({{ variant.percentage.toFixed(1) }}%)

							<!-- Status Indicators -->
							<v-chip
								v-if="variant.is_control"
								size="x-small"
								color="success"
								label
								class="ml-1"
							>
								Control
							</v-chip>

							<v-chip
								v-if="variant.is_locked && autoBalanceEnabled"
								size="x-small"
								color="warning"
								label
								class="ml-1"
							>
								Locked
							</v-chip>
						</div>
					</div>
				</div>
			</template>
		</v-card-text>
	</v-card>
	<v-expand-transition>
		<!-- Percentage Error Alert -->
		<v-alert
			v-if="percentageError && test.variants.length > 0"
			type="error"
			variant="tonal"
			density="compact"
			icon="fa-solid fa-triangle-exclamation"
			class="mt-4"
			border="start"
		>
			<strong>Percentage total is not 100%:</strong> {{ percentageError }}
		</v-alert>
	</v-expand-transition>
</template>

<style scoped lang="scss">
.ab-test-card {
	overflow: hidden;

	.panel-header-content {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		width: 100%;
	}

	.test-info {
		display: flex;
		flex-direction: column;

		.test-title {
			display: flex;
			align-items: center;

			.test-icon {
				margin-right: 8px;
				color: #1976d2;
			}

			.test-name {
				font-size: 1.05rem;
				font-weight: 600;
				color: #424242;
			}
		}

		.test-description {
			font-size: 0.85rem;
			color: #757575;
			margin-top: 4px;
			margin-left: 28px;
			text-overflow: ellipsis;
			overflow: hidden;
			max-width: 35rem;
			white-space: nowrap;
		}
	}

	.panel-content {
		padding: 16px;

		.no-variants {
			padding: 16px;
			text-align: center;
			color: #757575;
			font-style: italic;
			background-color: #f5f5f5;
			border-radius: 4px;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.variants-list {
			display: flex;
			flex-direction: column;
			gap: 12px;

			.variant-item {
				display: grid;
				grid-template-columns: 1fr 2fr auto;
				align-items: center;
				gap: 16px;
				padding: 8px 12px;
				border-radius: 8px;
				background-color: rgba(0, 0, 0, 0.02);
				transition: all 0.15s ease;

				&:hover {
					background-color: rgba(0, 0, 0, 0.04);
				}

				&.control-variant {
					background-color: rgba(76, 175, 80, 0.04);
					border-left: 3px solid #4caf50;

					&:hover {
						background-color: rgba(76, 175, 80, 0.08);
					}
				}

				&.locked-variant {
					background-color: rgba(251, 140, 0, 0.04);

					&:not(.control-variant) {
						border-left: 3px solid #fb8c00;
					}

					&:hover {
						background-color: rgba(251, 140, 0, 0.08);
					}
				}

				.variant-info {
					display: flex;
					align-items: center;
					gap: 12px;

					.variant-name-field {
						flex-grow: 1;
						min-width: 0;
					}

					.percentage-actions {
						display: flex;
						align-items: center;

						.lock-btn {
							min-width: 32px;
							padding: 0;
							margin-right: 2px;
						}
					}

					.percentage-control {
						position: relative;

						.variant-chip {
							flex-shrink: 0;
							min-width: 65px;
							border-radius: 4px;
							justify-content: center;
							cursor: pointer;
							transition: all 0.2s ease;

							&:hover:not([disabled]) {
								opacity: 0.9;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
							}

							&[disabled] {
								cursor: default;
							}
						}

						.percentage-input {
							width: 100%;

							:deep(.v-field__input) {
								padding-top: 4px;
								padding-bottom: 4px;
								min-height: 32px;
							}
						}
					}
				}

				.variant-actions {
					display: flex;
					flex-wrap: nowrap;
					gap: 4px;
					justify-content: flex-end;

					.action-btn-small {
						opacity: 0.6;
						transition: opacity 0.15s ease;

						&:hover {
							opacity: 1;
						}
					}
				}
			}
		}

		.variants-summary {
			.percentage-bar {
				height: 12px;
				width: 100%;
				display: flex;
				border-radius: 6px;
				overflow: hidden;
				box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);

				.percentage-segment {
					transition: width 0.3s ease;
					height: 100%;
					min-width: 2px;

					&.locked-segment {
						position: relative;

						&::after {
							content: "";
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
							background-image: repeating-linear-gradient(
								-45deg,
								rgba(0, 0, 0, 0),
								rgba(0, 0, 0, 0) 4px,
								rgba(0, 0, 0, 0.1) 4px,
								rgba(0, 0, 0, 0.1) 8px
							);
						}
					}

					&.error-bar {
						position: relative;

						&::after {
							content: "";
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
							border: 2px solid rgba(244, 67, 54, 0.5);
							border-radius: 6px;
						}
					}
				}
			}

			.variant-labels {
				display: flex;
				flex-wrap: wrap;
				gap: 16px;
				margin-top: 8px;

				.variant-label {
					display: flex;
					align-items: center;
					font-size: 0.8rem;

					.color-dot {
						height: 8px;
						width: 8px;
						border-radius: 50%;
						display: inline-block;
						margin-right: 6px;
					}

					&.control-label,
					&.locked-label {
						font-weight: 500;
					}
				}
			}
		}
	}
}
</style>
